import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { 
  ArrowLeft, UserX, Loader2
} from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../config/api';

const API = `${API_URL}/api`;

export default function BlockedUsers() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { t } = useLanguage();
  
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unblocking, setUnblocking] = useState({});

  useEffect(() => {
    loadBlockedUsers();
  }, []);

  const loadBlockedUsers = async () => {
    try {
      const res = await axios.get(`${API}/social/blocked`, { withCredentials: true });
      setBlockedUsers(res.data?.blocked_users || []);
    } catch (err) {
      console.error('Error loading blocked users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUnblock = async (userId) => {
    setUnblocking(prev => ({ ...prev, [userId]: true }));
    
    try {
      await axios.post(`${API}/social/unblock`, { user_id: userId }, { withCredentials: true });
      setBlockedUsers(prev => prev.filter(u => u.user_id !== userId));
    } catch (err) {
      console.error('Error unblocking user:', err);
    } finally {
      setUnblocking(prev => ({ ...prev, [userId]: false }));
    }
  };

  return (
    <div className={`min-h-full ${isDark ? 'bg-[#0a0a0f]' : 'bg-gray-50'}`}
         style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      {/* Header */}
      <div className={`${isDark ? 'bg-[#0a0a0f]' : 'bg-white'} border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="flex items-center gap-3 px-4 py-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2" data-testid="back-btn">
            <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-900'}`} />
          </button>
          <h1 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('settings.blockedUsers') || 'Utenti Bloccati'}
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className={`w-8 h-8 animate-spin ${isDark ? 'text-white/50' : 'text-gray-400'}`} />
          </div>
        ) : blockedUsers.length > 0 ? (
          <div className="space-y-2">
            {blockedUsers.map((user) => (
              <div 
                key={user.user_id}
                className={`flex items-center gap-3 p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'}`}
              >
                {/* Avatar */}
                <div className={`w-12 h-12 rounded-full overflow-hidden flex-shrink-0 ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
                  {user.profile_photo ? (
                    <img src={user.profile_photo} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className={`text-lg font-bold ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
                        {user.display_name?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1">
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {user.display_name}
                  </span>
                </div>

                {/* Unblock Button */}
                <button
                  onClick={() => handleUnblock(user.user_id)}
                  disabled={unblocking[user.user_id]}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  data-testid={`unblock-btn-${user.user_id}`}
                >
                  {unblocking[user.user_id] ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    t('social.unblock') || 'Sblocca'
                  )}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <UserX className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
            <p className={isDark ? 'text-white/60' : 'text-gray-500'}>
              {t('social.noBlockedUsers') || 'Nessun utente bloccato'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
